import { useEffect, useMemo, useRef, useState } from 'react';
import { useModal } from 'hooks/3catinfo/useModal';
import Slider from 'components/3catinfo/Slider';
import Imatge from 'components/3catinfo/Imatge';
import Icona from 'components/3catinfo/ui/Icona';
import __isEmpty from 'lodash/isEmpty';
import clsx from 'clsx';
import styles from './sliderimatges.module.scss';

const normalizeImageSrc = (src = '') => {
    if (!src) return '';
    if (src.startsWith('//')) return `https:${src}`;
    if (src.startsWith('http://')) return src.replace('http://', 'https://');
    return src;
};

const mapImageItem = (item, index) => {
    if (typeof item === 'string') {
        return {
            id: `img-${index}`,
            src: normalizeImageSrc(item),
            alt: `Imatge ${index + 1}`,
            caption: ''
        };
    }

    const src =
        item?.src ||
        item?.url ||
        item?.fitxer ||
        item?.image?.src ||
        item?.imatge?.src ||
        item?.imatge?.text ||
        item?.imatge?.url ||
        '';
    const alt = item?.alt || item?.title || item?.titol || item?.nom || `Imatge ${index + 1}`;
    const caption = item?.caption || item?.peu || item?.descripcio || '';

    return {
        id: item?.id || `img-${index}`,
        src: normalizeImageSrc(src),
        alt,
        caption
    };
};

const LightboxImatges = (props) => {
    const { images, initialIndex = 0, onClickClose, tipus = 'keyframe' } = props;
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const closeButtonRef = useRef(null);
    const image = images[currentIndex];
    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex < images.length - 1;

    useEffect(() => {
        closeButtonRef.current?.focus();
    }, [currentIndex]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'ArrowLeft' && hasPrev) {
                event.preventDefault();
                setCurrentIndex((prev) => prev - 1);
            }

            if (event.key === 'ArrowRight' && hasNext) {
                event.preventDefault();
                setCurrentIndex((prev) => prev + 1);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [hasPrev, hasNext]);

    const stylesImatge = clsx(styles.lightboxImageWrapper, tipus && styles[tipus]);

    return (
        <div className={styles.lightboxMask}>
            <button
                className={styles.lightboxClose}
                type='button'
                onClick={onClickClose}
                ref={closeButtonRef}
                aria-label='Tanca la imatge ampliada'
            >
                <Icona icona='tanca' isBlanc={true} alt='Tanca' />
            </button>

            {hasPrev && (
                <button
                    type='button'
                    className={clsx(styles.lightboxNav, styles.lightboxNavPrev)}
                    onClick={() => setCurrentIndex((prev) => prev - 1)}
                    aria-label='Imatge anterior'
                >
                    <Icona icona='fletxa_simple_esquerra' alt='Anterior' mida={24} inline={true} />
                </button>
            )}

            {hasNext && (
                <button
                    type='button'
                    className={clsx(styles.lightboxNav, styles.lightboxNavNext)}
                    onClick={() => setCurrentIndex((prev) => prev + 1)}
                    aria-label='Imatge següent'
                >
                    <Icona icona='fletxa_simple_dreta' alt='Següent' mida={24} inline={true} />
                </button>
            )}

            <div className={styles.lightboxContent}>
                <div className={stylesImatge}>
                    <Imatge src={image?.src} alt={image?.alt} layout='fill' objectFit='contain' priority={true} />
                </div>
                <div className={styles.lightboxInfo}>
                    {image?.caption && <span className={styles.lightboxCaption}>{image.caption}</span>}
                    <span className={styles.lightboxCounter}>
                        {currentIndex + 1} / {images.length}
                    </span>
                </div>
            </div>
        </div>
    );
};

const SliderImatges = (props) => {
    const {
        items = [],
        titol,
        descripcio,
        fletxes = 'mig',
        estilFletxes,
        noTitol = false,
        estilModul = 'sliderImatges',
        tipus = 'keyframe'
    } = props;
    const { setModal } = useModal();

    const images = useMemo(() => {
        return (items || []).map((item, index) => mapImageItem(item, index)).filter((item) => !__isEmpty(item.src));
    }, [items]);

    const openLightbox = (initialIndex) => {
        setModal(<LightboxImatges images={images} initialIndex={initialIndex} tipus={tipus} />, {
            mode: 'full-screen',
            closeOnClickOutside: true,
            className: styles.modalLightbox
        });
    };

    const styleImatge = clsx(styles.thumbnailImageWrapper, tipus && styles[tipus]);

    if (__isEmpty(images)) return <></>;

    return (
        <div className={styles.sliderImatges}>
            <Slider
                format='slider_imatges_lightbox'
                fletxes={fletxes}
                titol={titol}
                descripcio={descripcio}
                noTitol={noTitol}
                estilModul={estilModul}
                estilFletxes={estilFletxes}
            >
                {images.map((image, index) => (
                    <div className={styles.slide} key={`slide-imatge-${image.id}-${index}`}>
                        <button
                            type='button'
                            className={styles.thumbnailButton}
                            onClick={() => openLightbox(index)}
                            aria-label={`Amplia ${image.alt}`}
                        >
                            <div className={styleImatge}>
                                <Imatge src={image.src} alt={image.alt} layout='fill' objectFit='cover' />
                            </div>
                            {image.caption && <span className={styles.thumbnailCaption}>{image.caption}</span>}
                        </button>
                    </div>
                ))}
            </Slider>
        </div>
    );
};

export default SliderImatges;
